/**
 * SSH Access Component
 * 
 * Displays SSH access status and provides certificate generation for edge devices
 */

import React, { useState, useEffect } from 'react';
import { useToast } from '../../components/common/ToastContainer';
import edgeService from '../../services/edge.service';
import { useTenantStore } from '../../stores/tenantStore';
import type { SSHStatusResponse, SSHTunnelKeyResponse, SSHUserCertResponse } from '../../services/edge.service';
import { CheckCircle, XCircle, Loader, Key, Terminal, Copy, Download, Info, Play, RotateCw, Square, Activity, RefreshCcw } from 'lucide-react';
import PublicKeyModal from './PublicKeyModal';

interface SSHAccessProps {
  edgeGuid: string;
  edgeStatus?: string;
}

export const SSHAccess: React.FC<SSHAccessProps> = ({ edgeGuid, edgeStatus }) => {
  const toast = useToast();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const [status, setStatus] = useState<SSHStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [tunnelKeyLoading, setTunnelKeyLoading] = useState(false);
  const [userCertLoading, setUserCertLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tunnelKey, setTunnelKey] = useState<SSHTunnelKeyResponse | null>(null);
  const [userCert, setUserCert] = useState<SSHUserCertResponse | null>(null);
  const [isPublicKeyModalOpen, setIsPublicKeyModalOpen] = useState(false);
  const [mqttCommandLoading, setMqttCommandLoading] = useState<string | null>(null);
  const [reinitLoading, setReinitLoading] = useState(false);
  const [showReinitConfirm, setShowReinitConfirm] = useState(false);

  const fetchStatus = async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    setError(null);
    try {
      const options = isBackground ? { showLoading: false } : undefined;
      const response = await edgeService.getSSHStatus(edgeGuid, options);
      setStatus(response);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch SSH status');
      setStatus(null);
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  useEffect(() => {
    // Always try to fetch status - API will handle if edge is not provisioned
    fetchStatus(false);
    const interval = setInterval(() => fetchStatus(true), 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, [edgeGuid, selectedTenantId]);

  const handleGenerateTunnelKey = async () => {
    setTunnelKeyLoading(true);
    try {
      const response = await edgeService.generateSSHTunnelKey(edgeGuid);
      setTunnelKey(response);
      toast.success('Tunnel Key Generated', 'Key generated, authorized on bastion, and push initiated to edge. The tunnel will connect when the edge receives the key.');
      // Poll status more aggressively after deployment
      setTimeout(() => fetchStatus(), 3000);
      setTimeout(() => fetchStatus(), 8000);
      setTimeout(() => fetchStatus(), 15000);
    } catch (err: any) {
      toast.error('Tunnel Key Generation Failed', err.message || 'Failed to generate SSH tunnel key');
    } finally {
      setTunnelKeyLoading(false);
    }
  };

  const handleGenerateUserCert = async (selection: {
    publicKey?: string;
    userKeyId?: string;
    keyLabel?: string;
    privateKeyPath?: string;
  }) => {
    setUserCertLoading(true);
    try {
      const arg = selection.userKeyId
        ? { userKeyId: selection.userKeyId } as const
        : { publicKey: selection.publicKey! } as const;
      // Paste-flow meta (label + path) is opportunistic — the backend
      // auto-saves the pasted key to the user's profile so they can pick
      // it from the saved-key list next time.
      const pasteMeta = !selection.userKeyId
        ? { keyLabel: selection.keyLabel, privateKeyPath: selection.privateKeyPath }
        : undefined;
      const response = await edgeService.generateSSHUserCert(arg, undefined, edgeGuid, pasteMeta);
      setUserCert(response);
      setIsPublicKeyModalOpen(false);
      toast.success('SSH User Certificate Generated', 'Certificate valid for 24 hours.');
    } catch (err: any) {
      toast.error('Certificate Generation Failed', err.message || 'Failed to generate SSH user certificate');
    } finally {
      setUserCertLoading(false);
    }
  };

  const handleMQTTCommand = async (command: 'start' | 'restart' | 'stop' | 'health-check') => {
    setMqttCommandLoading(command);
    try {
      let result;
      switch (command) {
        case 'start':
          result = await edgeService.startSSHTunnel(edgeGuid);
          toast.success('Tunnel Start Command Sent', 'Edge will start the tunnel');
          break;
        case 'restart':
          result = await edgeService.restartSSHTunnel(edgeGuid);
          toast.success('Tunnel Restart Command Sent', 'Edge will restart the tunnel');
          break;
        case 'stop':
          result = await edgeService.stopSSHTunnel(edgeGuid);
          toast.success('Tunnel Stop Command Sent', 'Edge will stop the tunnel');
          break;
        case 'health-check': {
          result = await edgeService.getSSHTunnelHealthCheck(edgeGuid);
          // The edge answered. "Tunnel not active" is a valid health result
          // (backend returns 200 with active:false), not an error — show it as
          // an informational toast rather than a failure prompt.
          const health = result?.data ?? {};
          if (health.active === false) {
            toast.warning('Tunnel Not Active', health.message || 'The SSH tunnel is not currently active on this edge.');
          } else {
            toast.success('Tunnel Healthy', 'The edge reports the SSH tunnel is active.');
          }
          break;
        }
      }

      // Refresh status after command
      setTimeout(() => fetchStatus(), 2000);
    } catch (err: any) {
      // Surface the orchestrator's own error message (e.g. the edge's reply, or
      // "MQTT broker not connected") instead of the generic axios
      // "Request failed with status code 5xx".
      const backendMsg = err?.response?.data?.error || err?.message || `Failed to ${command} tunnel`;
      toast.error('MQTT Command Failed', backendMsg);
    } finally {
      setMqttCommandLoading(null);
    }
  };

  const handleReinitialize = async () => {
    setReinitLoading(true);
    setShowReinitConfirm(false);
    try {
      const result = await edgeService.reinitializeSSHTunnel(edgeGuid);
      if (result.keyGenerated) {
        toast.success('Tunnel Reinitialized', 'New key generated and push initiated to edge.');
      } else {
        toast.success('Tunnel Reinitialized', 'Existing key re-pushed to edge. Tunnel will restart.');
      }
      setTimeout(() => fetchStatus(), 3000);
      setTimeout(() => fetchStatus(), 8000);
    } catch (err: any) {
      toast.error('Reinitialize Failed', err.message || 'Failed to reinitialize SSH tunnel');
    } finally {
      setReinitLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied', `${label} copied to clipboard`);
  };

  const downloadFile = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Downloaded', `${filename} downloaded`);
  };

  // Show warning if edge is not provisioned (if status is provided)
  if (edgeStatus && edgeStatus !== 'provisioned' && !loading && !status) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <XCircle className="h-5 w-5 text-yellow-600" />
          <div>
            <h3 className="font-semibold text-yellow-900">Edge Not Provisioned</h3>
            <p className="text-sm text-yellow-700 mt-1">
              Edge must be provisioned before SSH access can be configured.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader className="h-6 w-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading SSH status...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <XCircle className="h-5 w-5 text-red-600" />
          <div>
            <h3 className="font-semibold text-red-900">Error</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Setup Workflow Guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 mb-2">SSH Tunnel Setup</h3>
            <div className="space-y-2 text-sm text-blue-800">
              <div className="flex items-start gap-2">
                <span className="font-semibold min-w-[20px]">1.</span>
                <span>
                  <strong>Generate Tunnel Key</strong> - Creates keypair, authorizes on bastion, and deploys to edge via MQTT.
                  {status?.tunnelKeyExists ? (
                    <span className="ml-2 inline-flex items-center gap-1 text-green-700">
                      <CheckCircle className="h-4 w-4" />
                      <span className="font-medium">Complete</span>
                    </span>
                  ) : (
                    <span className="ml-2 inline-flex items-center gap-1 text-orange-700">
                      <XCircle className="h-4 w-4" />
                      <span className="font-medium">Required</span>
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold min-w-[20px]">2.</span>
                <span>
                  <strong>Tunnel Connects</strong> - Edge automatically establishes reverse tunnel to bastion after key deployment.
                  {status?.tunnelActive ? (
                    <span className="ml-2 inline-flex items-center gap-1 text-green-700">
                      <CheckCircle className="h-4 w-4" />
                      <span className="font-medium">Active</span>
                    </span>
                  ) : (
                    <span className="ml-2 inline-flex items-center gap-1 text-gray-600">
                      <XCircle className="h-4 w-4" />
                      <span className="font-medium">Waiting</span>
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold min-w-[20px]">3.</span>
                <span>
                  <strong>Generate User Certificate</strong> - Creates 24-hour certificate for admin SSH access.
                  {userCert ? (
                    <span className="ml-2 inline-flex items-center gap-1 text-green-700">
                      <CheckCircle className="h-4 w-4" />
                      <span className="font-medium">Generated</span>
                    </span>
                  ) : (
                    <span className="ml-2 inline-flex items-center gap-1 text-gray-600">
                      <XCircle className="h-4 w-4" />
                      <span className="font-medium">Optional</span>
                    </span>
                  )}
                </span>
              </div>
            </div>

            {!status?.tunnelKeyExists && (
              <div className="mt-3 pt-3 border-t border-blue-300">
                <p className="text-sm text-blue-800 font-medium">
                  Click "Generate Tunnel Key" below. The key will be automatically deployed to the edge via MQTT.
                </p>
              </div>
            )}

            {status?.tunnelKeyExists && !status?.tunnelActive && (
              <div className="mt-3 pt-3 border-t border-blue-300">
                <p className="text-sm text-blue-800 font-medium">
                  Tunnel key deployed. Waiting for edge to establish tunnel. If the edge is offline, the tunnel will connect when it comes back online. You can also use "Start Tunnel" below.
                </p>
              </div>
            )}

            {status?.tunnelActive && (
              <div className="mt-3 pt-3 border-t border-blue-300">
                <p className="text-sm text-blue-800 font-medium">
                  Tunnel is active. Generate a user certificate below to SSH into the edge via the bastion.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* SSH Status Overview */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Terminal className="h-5 w-5" />
          SSH Access Status
        </h3>

        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center gap-3">
            {status?.tunnelKeyExists ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-gray-400" />
            )}
            <div>
              <div className="font-medium">Tunnel Key</div>
              <div className="text-sm text-gray-600">
                {status?.tunnelKeyExists ? 'Generated' : 'Not generated'}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {status?.hostCertInstalled ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-gray-400" />
            )}
            <div>
              <div className="font-medium">Host Certificate</div>
              <div className="text-sm text-gray-600">
                {status?.hostCertInstalled ? 'Installed' : 'Not installed'}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {status?.tunnelActive ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-gray-400" />
            )}
            <div>
              <div className="font-medium">Reverse Tunnel</div>
              <div className="text-sm text-gray-600">
                {status?.tunnelActive ? 'Active' : 'Not active'}
              </div>
            </div>
          </div>
        </div>

        {status && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              <div>Bastion: <code className="bg-gray-100 px-2 py-1 rounded">{status.bastionHost}:{status.bastionPort}</code></div>
              <div className="mt-1">Tunnel Port: <code className="bg-gray-100 px-2 py-1 rounded">{status.tunnelPort}</code></div>
            </div>
          </div>
        )}
      </div>

      {/* MQTT Tunnel Control */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Remote Tunnel Management
        </h3>
        
        <p className="text-sm text-gray-600 mb-4">
          Control the SSH tunnel remotely via MQTT commands. Changes take effect immediately on the edge device.
        </p>

        <div className="flex gap-3">
          {!status?.tunnelActive && (
            <button
              onClick={() => handleMQTTCommand('start')}
              disabled={mqttCommandLoading !== null}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {mqttCommandLoading === 'start' ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Start Tunnel
                </>
              )}
            </button>
          )}
          
          {status?.tunnelActive && (
            <>
              <button
                onClick={() => handleMQTTCommand('restart')}
                disabled={mqttCommandLoading !== null}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {mqttCommandLoading === 'restart' ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Restarting...
                  </>
                ) : (
                  <>
                    <RotateCw className="h-4 w-4" />
                    Restart Tunnel
                  </>
                )}
              </button>
              
              <button
                onClick={() => handleMQTTCommand('stop')}
                disabled={mqttCommandLoading !== null}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {mqttCommandLoading === 'stop' ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Stopping...
                  </>
                ) : (
                  <>
                    <Square className="h-4 w-4" />
                    Stop Tunnel
                  </>
                )}
              </button>
            </>
          )}
          
          <button
            onClick={() => handleMQTTCommand('health-check')}
            disabled={mqttCommandLoading !== null}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {mqttCommandLoading === 'health-check' ? (
              <>
                <Loader className="h-4 w-4 animate-spin" />
                Checking...
              </>
            ) : (
              <>
                <Activity className="h-4 w-4" />
                Health Check
              </>
            )}
          </button>

          {/* Separator */}
          <div className="w-px bg-gray-300" />

          {/* Reinitialize — re-pushes keys from DB to edge */}
          <button
            onClick={() => setShowReinitConfirm(true)}
            disabled={mqttCommandLoading !== null || reinitLoading}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {reinitLoading ? (
              <>
                <Loader className="h-4 w-4 animate-spin" />
                Reinitializing...
              </>
            ) : (
              <>
                <RefreshCcw className="h-4 w-4" />
                Reinitialize Tunnel
              </>
            )}
          </button>
        </div>

        {/* Reinitialize Confirmation */}
        {showReinitConfirm && (
          <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-sm text-orange-800 mb-3">
              This will re-push the SSH tunnel keys from the server to the edge device. Use this if the edge is missing its tunnel keys or the tunnel won't connect after a key rotation. If no key exists, a new one will be generated.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleReinitialize}
                disabled={reinitLoading}
                className="px-3 py-1.5 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 disabled:opacity-50"
              >
                Confirm Reinitialize
              </button>
              <button
                onClick={() => setShowReinitConfirm(false)}
                className="px-3 py-1.5 bg-white text-gray-700 text-sm rounded border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tunnel Key Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Key className="h-5 w-5" />
          Reverse Tunnel Key
        </h3>

        {!status?.tunnelActive && (
          <div className="mb-4">
            <button
              onClick={handleGenerateTunnelKey}
              disabled={tunnelKeyLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {tunnelKeyLoading ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Generating &amp; Deploying...
                </>
              ) : (
                <>
                  <Key className="h-4 w-4" />
                  {status?.tunnelKeyExists ? 'Regenerate Tunnel Key' : 'Generate Tunnel Key'}
                </>
              )}
            </button>
            <p className="text-sm text-gray-600 mt-2">
              Generates a keypair, authorizes it on the bastion, and deploys it to the edge via MQTT. The tunnel starts automatically.
            </p>
          </div>
        )}

        {tunnelKey && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-900">Key generated and push initiated</p>
                  <p className="text-sm text-green-700">
                    Tunnel key authorized on bastion and push initiated to edge via MQTT.
                    Bastion: {tunnelKey.bastionHost}:{tunnelKey.bastionPort} | Tunnel Port: {tunnelKey.tunnelPort}
                  </p>
                  <p className="text-sm text-green-600 mt-1">
                    If the edge is online, the tunnel will connect automatically. Otherwise, use "Reinitialize Tunnel" when the edge comes back online.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Certificate Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Key className="h-5 w-5" />
          User Certificate
        </h3>

        <div className="mb-4">
          <button
            onClick={() => setIsPublicKeyModalOpen(true)}
            disabled={userCertLoading || !status?.tunnelActive}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Key className="h-4 w-4" />
            Generate User Certificate
          </button>
          <p className="text-sm text-gray-600 mt-2">
            Generate a 24-hour SSH user certificate to access the edge via the reverse tunnel.
          </p>
          {!status?.tunnelActive && (
            <p className="text-sm text-amber-600 mt-1">
              ⚠️ Tunnel must be active before generating user certificate.
            </p>
          )}
        </div>

        {userCert && (() => {
          // VSNP-693 SSH UX (Option 1): generate a ~/.ssh/config block and a
          // small install script. After the user pastes/runs once, daily
          // connect is just `ssh sl-edge-<short>`.
          const shortGuid = edgeGuid.split('-').pop() || edgeGuid.slice(-12);
          const hostAlias = `sl-edge-${shortGuid}`;
          const certFilename = `edge-${shortGuid}-cert.pub`;
          const certPath = `~/.ssh/securelink/${certFilename}`;
          const bastionHost = status?.bastionHost || 'ssh-bastion.vsm.netlinkz.dev';
          const bastionPort = status?.bastionPort || 22;
          const tunnelPort = status?.tunnelPort || 10022;
          const validUntil = userCert.validBefore
            ? new Date(userCert.validBefore * 1000).toISOString()
            : '24h';

          // VSNP-680 UX: backend tells us where the user's private key lives
          // (per-algo default unless they overrode it). Falls back to ed25519
          // for old responses that don't include the field, but recent
          // images always populate it.
          const identityFile = userCert.privateKeyPath || '~/.ssh/id_ed25519';

          // The ProxyJump inner SSH spawns a separate process that re-reads
          // ~/.ssh/config matched against the bastion Host pattern, NOT our
          // edge Host alias — so we must emit a Host block for the bastion
          // too, or admin's other agent keys get offered and exhaust
          // MaxAuthTries. (Verified 2026-05-08 on OVH.)
          //
          // VSNP-798: emit a *per-edge* bastion alias
          // (`<bastion>.<hostAlias>`, real host via `Hostname`) instead of a
          // shared `Host <bastion>` block. OpenSSH treats CertificateFile as
          // cumulative across every matching Host stanza, so a shared block
          // accumulated one cert per edge and offered all of them on every
          // bastion connection — exhausting MaxAuthTries (6) once 6+ edges
          // were touched, surfacing as a misleading "Certificate invalid:
          // expired". With a unique alias the ProxyJump matches exactly one
          // stanza → exactly one cert offered to the bastion, no accumulation.
          // Host-key checking still keys off the real `Hostname`, so
          // known_hosts has a single bastion entry shared by all aliases.
          const bastionAlias = `${bastionHost}.${hostAlias}`;
          const bastionHostBlock = `Host ${bastionAlias}
  Hostname     ${bastionHost}
  Port         ${bastionPort}
  User         edge
  IdentityFile ${identityFile}
  CertificateFile ${certPath}
  IdentitiesOnly yes
`;

          const sshConfigBlock = `# securelink: ${hostAlias} (edge=${edgeGuid}, expires ${validUntil})
${bastionHostBlock}
Host ${hostAlias}
  HostName     localhost
  Port         ${tunnelPort}
  User         edge
  ProxyJump    ${bastionAlias}
  IdentityFile ${identityFile}
  CertificateFile ${certPath}
  IdentitiesOnly yes
`;

          // PuTTY-mode legacy ProxyCommand form (for users whose SSH client
          // doesn't support ProxyJump — e.g. PuTTY, very old OpenSSH).
          const puttyCmd = `ssh -o IdentitiesOnly=yes -o IdentityFile=${identityFile} -o CertificateFile=${certPath} -o ProxyCommand="ssh -o IdentitiesOnly=yes -o IdentityFile=${identityFile} -o CertificateFile=${certPath} -W localhost:${tunnelPort} edge@${bastionHost}" edge@localhost`;

          // Install script: writes the cert + idempotently appends the SSH
          // config block. User runs it once, then `ssh ${hostAlias}` works.
          const installScript = `#!/bin/sh
# securelink-install-${hostAlias}.sh — generated by SecureLink
set -e

mkdir -p ~/.ssh/securelink
chmod 700 ~/.ssh ~/.ssh/securelink 2>/dev/null || true

# Write the certificate
cat > ${certPath} <<'CERT_EOF'
${userCert.certificate.trimEnd()}
CERT_EOF
chmod 644 ${certPath}
echo "✓ wrote certificate to ${certPath}"

# Idempotently append the SSH config block (skip if marker already present)
touch ~/.ssh/config && chmod 600 ~/.ssh/config
MARKER="# securelink: ${hostAlias}"
if grep -qF "$MARKER" ~/.ssh/config 2>/dev/null; then
  echo "ℹ ${hostAlias} block already present in ~/.ssh/config (cert was refreshed)"
else
  printf '\\n' >> ~/.ssh/config
  cat >> ~/.ssh/config <<'CFG_EOF'
${sshConfigBlock}CFG_EOF
  echo "✓ added ${hostAlias} block to ~/.ssh/config"
fi

echo ""
echo "Connect:  ssh ${hostAlias}"
`;

          return (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="font-medium text-green-900 mb-1">Certificate generated — valid until {userCert.validBefore ? new Date(userCert.validBefore * 1000).toLocaleString() : 'in 24 hours'}</p>
                <p className="text-sm text-green-700">After you install it once below, connect with: <code className="bg-green-100 px-1.5 py-0.5 rounded font-mono">ssh {hostAlias}</code></p>
              </div>

              {/* Quick install script (recommended) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quick install (recommended)
                </label>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                  <p className="text-sm text-blue-900">
                    Download the install script and run it once. It writes the cert to <code className="bg-white px-1 rounded text-xs">~/.ssh/securelink/</code> and appends the matching block to <code className="bg-white px-1 rounded text-xs">~/.ssh/config</code> (idempotent — re-running just refreshes the cert).
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => downloadFile(installScript, `securelink-install-${hostAlias}.sh`)}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Download install script
                    </button>
                    <button
                      onClick={() => copyToClipboard(`sh ./securelink-install-${hostAlias}.sh`, 'Run command')}
                      className="px-3 py-2 border border-gray-300 rounded hover:bg-gray-50 text-sm"
                    >
                      Copy run command
                    </button>
                  </div>
                  <p className="text-xs text-blue-700">
                    After running: <code className="bg-white px-1 rounded">ssh {hostAlias}</code>
                  </p>
                </div>
              </div>

              {/* Manual: copy SSH config + cert separately */}
              <details className="bg-gray-50 border border-gray-200 rounded-lg">
                <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 hover:bg-gray-100">
                  Manual install (copy SSH config block + cert separately)
                </summary>
                <div className="px-4 pb-4 pt-2 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ~/.ssh/config block (paste once)
                    </label>
                    <div className="flex gap-2">
                      <textarea
                        readOnly
                        value={sshConfigBlock}
                        className="flex-1 font-mono text-xs bg-white border border-gray-300 rounded p-3"
                        rows={9}
                      />
                      <button
                        onClick={() => copyToClipboard(sshConfigBlock, 'SSH config block')}
                        className="p-2 border border-gray-300 rounded hover:bg-gray-50 self-start"
                        title="Copy SSH config block"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Certificate (save as <code className="bg-white px-1 rounded text-xs">{certPath}</code>)
                    </label>
                    <div className="flex gap-2">
                      <textarea
                        readOnly
                        value={userCert.certificate}
                        className="flex-1 font-mono text-xs bg-white border border-gray-300 rounded p-3"
                        rows={4}
                      />
                      <div className="flex flex-col gap-2 self-start">
                        <button
                          onClick={() => copyToClipboard(userCert.certificate, 'Certificate')}
                          className="p-2 border border-gray-300 rounded hover:bg-gray-50"
                          title="Copy"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => downloadFile(userCert.certificate, certFilename)}
                          className="p-2 border border-gray-300 rounded hover:bg-gray-50"
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </details>

              {/* PuTTY mode (legacy ProxyCommand form) */}
              <details className="bg-gray-50 border border-gray-200 rounded-lg">
                <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 hover:bg-gray-100">
                  PuTTY mode (legacy single-command, no ProxyJump)
                </summary>
                <div className="px-4 pb-4 pt-2 space-y-2">
                  <p className="text-xs text-gray-600">
                    For SSH clients without ProxyJump support (PuTTY, very old OpenSSH). Save the cert from the manual section first, then run:
                  </p>
                  <div className="bg-white border border-gray-300 rounded p-3 flex items-start gap-2">
                    <code className="text-xs break-all flex-1 select-all font-mono">{puttyCmd}</code>
                    <button
                      onClick={() => copyToClipboard(puttyCmd, 'PuTTY-mode command')}
                      className="p-1.5 border border-gray-300 rounded hover:bg-gray-50 flex-shrink-0"
                      title="Copy"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </details>
            </div>
          );
        })()}
      </div>

      {/* Public Key Modal */}
      <PublicKeyModal
        isOpen={isPublicKeyModalOpen}
        onClose={() => setIsPublicKeyModalOpen(false)}
        onSubmit={handleGenerateUserCert}
        isLoading={userCertLoading}
      />
    </div>
  );
};

export default SSHAccess;

